'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import HealthBadge from '@/components/ui/HealthBadge';

const TwinTrendCharts = dynamic(() => import('./TwinTrendCharts'), { ssr: false });

type HealthStatus = 'healthy' | 'at-risk' | 'critical' | 'recovering' | 'optimized';

interface Twin {
  id: string;
  name: string;
  domain: string;
  healthScore: number;
  status: HealthStatus;
  riskLevel: string;
  trendDirection: 'improving' | 'declining' | 'stable';
  prediction: string;
  lastSimulation: string;
  activeCases: number;
  openRisks: number;
  conformance: number;
  cycleTime: string;
  valueAtRisk: string;
}

const twins: Twin[] = [
  {
    id: 'twin-fin-close',
    name: 'Financial Close',
    domain: 'Finance',
    healthScore: 82,
    status: 'healthy',
    riskLevel: 'Low',
    trendDirection: 'improving',
    prediction: 'Quarter close expected in 4.2 days — within target',
    lastSimulation: '1h ago',
    activeCases: 1,
    openRisks: 0,
    conformance: 91,
    cycleTime: '4.2d',
    valueAtRisk: '$0',
  },
  {
    id: 'twin-cash-app',
    name: 'Cash Application',
    domain: 'Finance',
    healthScore: 61,
    status: 'at-risk',
    riskLevel: 'High',
    trendDirection: 'declining',
    prediction: 'DSO projected to increase by 2.1 days if current trend continues',
    lastSimulation: '30min ago',
    activeCases: 847,
    openRisks: 4,
    conformance: 74,
    cycleTime: '3.8d',
    valueAtRisk: '$2.4M',
  },
  {
    id: 'twin-order-fulfill',
    name: 'Order Fulfillment',
    domain: 'Operations',
    healthScore: 44,
    status: 'critical',
    riskLevel: 'Critical',
    trendDirection: 'declining',
    prediction: '23% probability of SLA breach in next 48 hours based on current backlog',
    lastSimulation: '15min ago',
    activeCases: 2341,
    openRisks: 7,
    conformance: 63,
    cycleTime: '8.1d',
    valueAtRisk: '$5.1M',
  },
  {
    id: 'twin-inventory',
    name: 'Inventory Health',
    domain: 'Operations',
    healthScore: 91,
    status: 'optimized',
    riskLevel: 'None',
    trendDirection: 'stable',
    prediction: 'Inventory levels stable — no reorder triggers expected for 14 days',
    lastSimulation: '2h ago',
    activeCases: 312,
    openRisks: 0,
    conformance: 95,
    cycleTime: '2.1d',
    valueAtRisk: '$0',
  },
];

const simulationScenarios = [
  { id: 'sim-baseline', label: 'Baseline (Current)', description: 'No changes — project forward from current state' },
  { id: 'sim-auto', label: 'Automate Approval Step', description: 'Remove manual approval for cases under $10K' },
  { id: 'sim-resource', label: '+2 FTE Processing', description: 'Add two additional processing staff members' },
  { id: 'sim-priority', label: 'Priority Queue Enabled', description: 'Route high-value cases to priority processing lane' },
];

const simResults: Record<string, { healthDelta: number; cycleDelta: string; conformanceDelta: number; impact: string }> = {
  'sim-baseline': { healthDelta: 0, cycleDelta: '±0d', conformanceDelta: 0, impact: 'No change projected' },
  'sim-auto': { healthDelta: +14, cycleDelta: '-1.2d', conformanceDelta: +9, impact: 'Est. $1.1M value-at-risk reduction' },
  'sim-resource': { healthDelta: +8, cycleDelta: '-0.7d', conformanceDelta: +5, impact: 'Est. $640K value-at-risk reduction' },
  'sim-priority': { healthDelta: +11, cycleDelta: '-0.9d', conformanceDelta: +7, impact: 'Est. $890K value-at-risk reduction, SLA breach risk -14%' },
};

const recommendations: Record<string, string[]> = {
  'twin-order-fulfill': [
    'Activate priority routing for 847 orders with delivery date within 24 hours',
    'Investigate "Awaiting Stock" status — 31% of critical cases blocked here for >48h',
    'Review fulfillment variant B (23% of volume) — manual re-routing adds 2.3 days average',
    'Consider temporary capacity increase during peak backlog period',
  ],
  'twin-cash-app': [
    'Accelerate matching on 94 unmatched remittances totaling $2.4M',
    'Enable automated bank statement reconciliation to reduce manual matching time',
    'Review exception handling SLA — current avg resolution 3.8 days vs 2-day target',
  ],
  default: [
    'Schedule monthly twin health review with process owner',
    'Review conformance rules and update ideal path if business process has changed',
    'Analyze top variants for automation opportunity assessment',
  ],
};

export default function DigitalTwinWorkspaceContent() {
  const [activeTwin, setActiveTwin] = useState(twins[2]);
  const [activeScenario, setActiveScenario] = useState('sim-baseline');

  const simResult = simResults[activeScenario];
  const projectedHealth = activeTwin.healthScore + simResult.healthDelta;
  const recs = recommendations[activeTwin.id] || recommendations.default;

  return (
    <div className="space-y-6">
      {/* Twin Selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {twins.map((twin) => (
          <button
            key={twin.id}
            onClick={() => setActiveTwin(twin)}
            className={`text-left p-4 rounded-xl border transition-all duration-150 ${
              activeTwin.id === twin.id
                ? 'bg-primary/5 border-primary/40 card-glow' :'bg-card border-border hover:border-border/80 hover:bg-muted/20'
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">{twin.name}</p>
                <p className="text-2xs text-muted-foreground">{twin.domain}</p>
              </div>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                twin.healthScore >= 80 ? 'bg-positive/15' :
                twin.healthScore >= 60 ? 'bg-warning/15' : 'bg-negative/15'
              }`}>
                <span className={`text-xs font-bold font-tabular ${
                  twin.healthScore >= 80 ? 'text-positive' :
                  twin.healthScore >= 60 ? 'text-warning' : 'text-negative'
                }`}>{twin.healthScore}</span>
              </div>
            </div>
            <HealthBadge status={twin.status} size="sm" />
          </button>
        ))}
      </div>

      {/* Main Workspace */}
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {/* Left: Health Scorecard + Charts */}
        <div className="xl:col-span-2 space-y-5">
          {/* Scorecard */}
          <div className="bg-card rounded-xl card-glow p-5">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-lg font-semibold text-foreground">{activeTwin.name} Digital Twin</h2>
                  <HealthBadge status={activeTwin.status} />
                </div>
                <p className="text-xs text-muted-foreground max-w-lg leading-relaxed">{activeTwin.prediction}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-3xl font-bold text-foreground font-tabular">{activeTwin.healthScore}</p>
                <p className="text-xs text-muted-foreground">Health Score</p>
                <div className={`flex items-center gap-1 justify-end mt-1 text-xs ${
                  activeTwin.trendDirection === 'improving' ? 'text-positive' :
                  activeTwin.trendDirection === 'declining' ? 'text-negative' : 'text-muted-foreground'
                }`}>
                  <Icon
                    name={activeTwin.trendDirection === 'improving' ? 'ArrowTrendingUpIcon' : activeTwin.trendDirection === 'declining' ? 'ArrowTrendingDownIcon' : 'MinusIcon'}
                    size={12}
                  />
                  <span className="capitalize">{activeTwin.trendDirection}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Conformance', value: `${activeTwin.conformance}%`, color: activeTwin.conformance >= 85 ? 'text-positive' : activeTwin.conformance >= 70 ? 'text-warning' : 'text-negative' },
                { label: 'Cycle Time P95', value: activeTwin.cycleTime, color: 'text-foreground' },
                { label: 'Active Cases', value: activeTwin.activeCases.toLocaleString(), color: 'text-foreground' },
                { label: 'Value at Risk', value: activeTwin.valueAtRisk, color: activeTwin.valueAtRisk === '$0' ? 'text-positive' : 'text-negative' },
              ].map((item) => (
                <div key={`scorecard-${item.label}`} className="bg-muted/40 rounded-lg p-3">
                  <p className="text-2xs text-muted-foreground mb-1">{item.label}</p>
                  <p className={`text-sm font-bold font-tabular ${item.color}`}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trend Charts */}
          <TwinTrendCharts twinId={activeTwin.id} />
        </div>

        {/* Right: Simulation + Recommendations */}
        <div className="space-y-5">
          {/* Simulation Studio */}
          <div className="bg-card rounded-xl card-glow-accent overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="BeakerIcon" size={16} className="text-accent" />
                <h3 className="text-sm font-semibold text-foreground">Scenario Simulation</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Compare what-if scenarios against baseline</p>
            </div>

            <div className="p-4 space-y-2">
              {simulationScenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  onClick={() => setActiveScenario(scenario.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all duration-150 ${
                    activeScenario === scenario.id
                      ? 'bg-accent/10 border-accent/30' :'bg-muted/30 border-border hover:border-border/60'
                  }`}
                >
                  <p className={`text-xs font-medium ${activeScenario === scenario.id ? 'text-accent' : 'text-foreground'}`}>
                    {scenario.label}
                  </p>
                  <p className="text-2xs text-muted-foreground mt-0.5">{scenario.description}</p>
                </button>
              ))}
            </div>

            {/* Simulation Results */}
            <div className="px-4 pb-4">
              <div className="bg-muted/30 rounded-lg p-4 border border-border">
                <p className="text-xs font-semibold text-foreground mb-3">Projected Outcome</p>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Health Score</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-tabular text-muted-foreground">{activeTwin.healthScore}</span>
                      <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />
                      <span className={`text-xs font-bold font-tabular ${
                        projectedHealth > activeTwin.healthScore ? 'text-positive' :
                        projectedHealth < activeTwin.healthScore ? 'text-negative' : 'text-foreground'
                      }`}>{projectedHealth}</span>
                      {simResult.healthDelta !== 0 && (
                        <span className={`text-2xs ${simResult.healthDelta > 0 ? 'text-positive' : 'text-negative'}`}>
                          ({simResult.healthDelta > 0 ? '+' : ''}{simResult.healthDelta})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Cycle Time</span>
                    <span className={`text-xs font-semibold font-tabular ${simResult.cycleDelta.startsWith('-') ? 'text-positive' : 'text-muted-foreground'}`}>
                      {simResult.cycleDelta}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Conformance</span>
                    <span className={`text-xs font-semibold font-tabular ${simResult.conformanceDelta > 0 ? 'text-positive' : 'text-muted-foreground'}`}>
                      {simResult.conformanceDelta > 0 ? '+' : ''}{simResult.conformanceDelta}%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <p className="text-xs text-accent font-medium">{simResult.impact}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-card rounded-xl card-glow overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Icon name="LightBulbIcon" size={16} className="text-warning" />
              <h3 className="text-sm font-semibold text-foreground">Recommendations</h3>
              <span className="ml-auto bg-warning/15 text-warning text-2xs font-semibold px-2 py-0.5 rounded-full">{recs.length}</span>
            </div>
            <div className="p-4 space-y-3">
              {recs.map((rec, i) => (
                <div key={`twin-rec-${activeTwin.id}-${i}`} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-warning/15 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-2xs font-bold text-warning">{i + 1}</span>
                  </div>
                  <p className="text-xs text-foreground leading-relaxed flex-1">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}